import { SortDirection } from '@air/api/types';

import { GENERAL_LIBRARY_ID, ITEM_PICKER_BOARD_FETCH_PARAMS } from '~/components/ItemPicker/itemPickerConstants';
import { type ItemPickerNavView } from '~/components/ItemPicker/itemPickerTypes';
import { type UsePrivateBoardsListParams } from '~/components/v2/hooks/queries/usePrivateBoardsList';

const CUSTOM_SORT_DESC = { direction: SortDirection.desc, name: 'customSort' } as const;
const RECENT_SORT_DESC = { direction: SortDirection.desc, name: 'dateCreated' } as const;

export type GetItemPickerBoardsListParamsInput = {
  navView: ItemPickerNavView;
  search: string;
  workspaceId: string | undefined;
};

const withOptionalSearch = (search: string): Pick<UsePrivateBoardsListParams, 'search'> => (search ? { search } : {});

/**
 * Boards.list params for the picker's current nav view.
 *
 * Search is omitted when empty so the API applies no title filter. A non-empty Search-view query
 * uses `libraryBoards: 'ALL'` and `view: 'all'` so boards inside named libraries are findable —
 * the empty Search view still lists general-library roots only (FOUND-813).
 */
export const getItemPickerBoardsListParams = ({
  navView,
  search,
  workspaceId,
}: GetItemPickerBoardsListParamsInput): UsePrivateBoardsListParams => {
  const base: UsePrivateBoardsListParams = {
    ...ITEM_PICKER_BOARD_FETCH_PARAMS,
    ...withOptionalSearch(search),
    sortField: CUSTOM_SORT_DESC,
    view: search ? 'all' : 'root',
    workspaceId,
  };

  switch (navView.kind) {
    case 'all':
      return {
        ...base,
        // Empty Search shows general-library roots; typing a term searches the whole workspace,
        // including boards nested in named libraries (the 20k-board Zagg case).
        libraryBoards: search ? 'ALL' : 'NO',
      };
    case 'favorites':
      return {
        ...base,
        // Match the app's favorites list: membership is the filter, not root/general-library.
        isMemberOfBoard: 'YES',
        libraryBoards: 'ALL',
        view: 'all',
      };
    case 'recent':
      return {
        ...base,
        libraryBoards: 'ALL',
        sortField: RECENT_SORT_DESC,
        view: 'all',
      };
    case 'library':
      if (navView.libraryId === GENERAL_LIBRARY_ID) {
        return {
          ...base,
          libraryBoards: 'NO',
        };
      }

      return {
        ...base,
        libraryBoards: 'YES',
        libraryIds: [navView.libraryId],
      };
    case 'board': {
      const libraryId = navView.libraryId;
      const isNamedLibrary = !!libraryId && libraryId !== GENERAL_LIBRARY_ID;
      const isGeneralLibrary = libraryId === GENERAL_LIBRARY_ID;

      // Searching inside a board uses ancestorCutoff so nested titles match; an empty query
      // lists direct children via `view: boardId`.
      //
      // When libraryId is unknown (Search / favorites / recents drill-in without board.library),
      // do not set libraryBoards: 'NO' — that filter drops named-library children (FOUND-813).
      if (search) {
        if (isNamedLibrary) {
          return {
            ...base,
            ancestorCutoff: navView.boardId,
            libraryBoards: 'YES',
            libraryIds: [libraryId],
            view: 'all',
          };
        }

        if (isGeneralLibrary) {
          return {
            ...base,
            ancestorCutoff: navView.boardId,
            libraryBoards: 'NO',
            view: 'all',
          };
        }

        return {
          ...base,
          ancestorCutoff: navView.boardId,
          view: 'all',
        };
      }

      if (isNamedLibrary) {
        return {
          ...base,
          libraryBoards: 'YES',
          libraryIds: [libraryId],
          view: navView.boardId,
        };
      }

      if (isGeneralLibrary) {
        return {
          ...base,
          libraryBoards: 'NO',
          view: navView.boardId,
        };
      }

      return {
        ...base,
        view: navView.boardId,
      };
    }
  }
};
